import { apiClient, ApiError, getAuthToken } from './client';
import { getErrorMessage } from './errors';
import type { FeatureFlagDto, QueryRequest, QueryResult } from '@flowqa/shared';

type FeatureFlagResolveResponse = { flags: Array<{ key: string; enabled: boolean }> };

export type FeatureFlagResolveResult = { flags: Record<string, boolean> };

export type FeatureFlagUpsertInput = {
  key: string;
  enabled: boolean;
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const resolveFeatureFlags = async (
  keys: string[],
  tokenOverride?: string | null,
): Promise<FeatureFlagResolveResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    const response = await apiClient.post<FeatureFlagResolveResponse>(
      'feature-flags/resolve',
      { keys },
      { retries: 1, token: authToken },
    );
    return {
      flags: response.flags.reduce<Record<string, boolean>>((acc, flag) => {
        acc[flag.key] = flag.enabled;
        return acc;
      }, {}),
    };
  } catch (err) {
    throw toApiError(err);
  }
};

export const resolvePublicFeatureFlags = async (
  keys: string[],
): Promise<FeatureFlagResolveResult> => {
  try {
    const response = await apiClient.post<FeatureFlagResolveResponse>(
      'public/feature-flags/resolve',
      { keys },
      { retries: 1 },
    );
    return {
      flags: response.flags.reduce<Record<string, boolean>>((acc, flag) => {
        acc[flag.key] = flag.enabled;
        return acc;
      }, {}),
    };
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryFeatureFlags = async (
  input: QueryRequest,
  tokenOverride?: string | null,
): Promise<QueryResult<FeatureFlagDto>> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<QueryResult<FeatureFlagDto>>('feature-flags/query', input, {
      retries: 1,
      token: authToken,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const upsertFeatureFlag = async (
  input: FeatureFlagUpsertInput,
  tokenOverride?: string | null,
): Promise<FeatureFlagDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<FeatureFlagDto>('feature-flags', input, {
      retries: 1,
      token: authToken,
    });
  } catch (err) {
    throw toApiError(err);
  }
};
